import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconAlertTriangle,
  IconBabyCarriage,
  IconBook,
  IconBriefcase,
  IconBuilding,
  IconBuildingCommunity,
  IconBuildingHospital,
  IconCar,
  IconCertificate,
  IconClipboardList,
  IconClock,
  IconConfetti,
  IconCurrencyDollar,
  IconDeviceMobile,
  IconFileText,
  IconHammer,
  IconHome,
  IconIdBadge2,
  IconPaw,
  IconPhone,
  IconPin,
  IconPlane,
  IconScale,
  IconSearch,
  IconStar,
  IconToolsKitchen2,
  IconUsers,
  IconWifi,
} from '@tabler/icons-react';

import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MingoIcon from '../design-system/icons/MingoIcon';
import { CATEGORY_ICON_KEYS } from '../data/mingoServiceCatalog';
import { useMingo } from '../context/MingoContext';
import { getLocalizedText } from '../api/translationApi';
import { getSafePathLifePack } from '../lib/safepathLifeInfoData';
import { resolveLifeInfoLocation } from '../lib/safepathLifeInfoRegion';
import { withReturnState } from '../lib/goBack';
import { renderUiIcon } from '../lib/renderUiIcon';

const LS_FAV = 'mingo_lifeinfo_fav_v1';
const LS_UI = 'mingo_lifeinfo_ui_v1';

const CATEGORY_FLAG = {
  hospital: 'local_hospital',
  food: 'food_mart',
  visa: 'local_visa_agency',
  beauty: 'local_beauty',
  move: 'local_cleaning',
  realestate: 'real_estate',
  rental: 'rental',
  airportPickup: 'local_airport_pickup',
  carpool: 'local_carpool',
  professional: 'professional',
  academy: 'academy',
  activity: 'local_activity',
  pet: 'local_pet',
  koreanShop: 'local_korean_shop',
  wedding: 'local_wedding',
  deals: 'deals',
  settlement: 'local_settlement',
  insurance: 'local_insurance',
  remittance: 'local_remittance',
  schools: 'local_schools',
  educationConsult: 'local_education_consult',
  fitness: 'local_fitness',
  golf: 'golf',
  medicalTourism: 'medical_tourism',
  boat: 'local_boat',
  photo: 'local_photo',
  eventPlanner: 'local_event_planner',
  digital: 'local_digital',
  tours: 'local_tours',
  interior: 'local_interior',
  subscription: 'local_subscription',
  coworking: 'coworking',
  cooking: 'recipes',
  visaRun: 'visa_run',
  businessTransfer: 'business_transfer',
  news: 'news',
  repatriate: 'local_return',
  jobs: 'jobs',
};

const SECTION_EMERGENCY = {
  key: 'emergency',
  labelKey: 'local.emergencySection',
  items: [
    { key: 'emergency', icon: IconPhone, labelKey: 'local.emergency', to: '/sos?tab=contacts' },
    { key: 'lostFound', icon: IconSearch, labelKey: 'local.lostFound', to: '/lost-found' },
    { key: 'expiry', icon: IconClock, labelKey: 'local.expiry', to: '/expiry-reminder' },
  ],
};

/** SafePath 지역별 10가지 — slug → 그리드 아이콘 */
const SAFE_PATH_SLUG_ICONS = {
  'business-tax': IconBriefcase,
  'family-legal': IconUsers,
  'education-admissions': IconCertificate,
  'visa-residence': IconClipboardList,
  'finance-banking': IconCurrencyDollar,
  'medical-safety': IconBuildingHospital,
  'real-estate-settlement': IconHome,
  'mobility-licenses': IconCar,
  'documents-notary': IconFileText,
  'public-community': IconBuildingCommunity,
};

const EXPAT_URGENT_ITEMS = [
  { key: 'passport', icon: IconIdBadge2, labelKey: 'expatInfo.quick.passportLoss', to: '/expat-info/category/emergency' },
  { key: 'accident', icon: IconCar, labelKey: 'expatInfo.quick.accident', to: '/expat-info/category/emergency' },
  { key: 'fraud', icon: IconAlertTriangle, labelKey: 'expatInfo.quick.fraud', to: '/expat-info/category/emergency' },
];

const HUB_SECTIONS = [
  {
    key: 'business_hub_v2',
    titleKey: 'life.favoritesSection',
    items: [
      { key: 'food_cafe_mart', icon: IconToolsKitchen2, labelKey: 'business.cats.food_cafe_mart', to: '/hub/food_cafe_mart' },
      { key: 'living_realestate_rental', icon: IconHome, labelKey: 'business.cats.living_realestate_rental', to: '/real-estate' },
      { key: 'home_interior', icon: IconHammer, labelKey: 'business.cats.home_interior', to: '/hub/home_interior' },
      { key: 'travel_activity_experience', icon: IconPlane, labelKey: 'business.cats.travel_activity_experience', to: '/hub/travel_activity_experience' },
      { key: 'accommodation_stay', icon: IconBuilding, labelKey: 'business.cats.accommodation_stay', to: '/hub/accommodation_stay' },
      { key: 'jobs_business', icon: IconBriefcase, labelKey: 'business.cats.jobs_business', to: '/hub/jobs_business' },
      { key: 'professional_services', icon: IconScale, labelKey: 'business.cats.professional_services', to: '/hub/professional_services' },
      { key: 'education_lesson', icon: IconCertificate, labelKey: 'business.cats.education_lesson', to: '/hub/education_lesson' },
      { key: 'beauty_wellness', icon: IconBuildingHospital, labelKey: 'business.cats.beauty_wellness', to: '/hub/beauty_wellness' },
      { key: 'health_medical', icon: IconBuildingHospital, labelKey: 'business.cats.health_medical', to: '/hub/health_medical' },
      { key: 'mobility_transport', icon: IconCar, labelKey: 'business.cats.mobility_transport', to: '/hub/mobility_transport' },
      { key: 'telecom', icon: IconWifi, labelKey: 'business.cats.telecom', to: '/hub/telecom' },
      { key: 'finance_remittance', icon: IconCurrencyDollar, labelKey: 'business.cats.finance_remittance', to: '/hub/finance_remittance' },
      { key: 'wedding_event', icon: IconConfetti, labelKey: 'business.cats.wedding_event', to: '/hub/wedding_event' },
      { key: 'pet', icon: IconPaw, labelKey: 'business.cats.pet', to: '/hub/pet' },
      { key: 'creator_media', icon: IconDeviceMobile, labelKey: 'business.cats.creator_media', to: '/hub/creator_media' },
      { key: 'parenting_care', icon: IconBabyCarriage, labelKey: 'business.cats.parenting_care', to: '/hub/parenting_care' },
    ],
  },
];

function favKey(c) {
  return `${c.to}::${c.key}`;
}

/** 14개 대분류는 전체서비스와 동일한 MingoIcon, 그 외는 기존 이모지로 표시 */
function CardIcon({ item }) {
  const iconKey = CATEGORY_ICON_KEYS[item.key];
  if (iconKey) {
    return (
      <MingoIcon
        iconKey={iconKey}
        variant="squircle"
        size="lg"
        className="mingo-local-info-card__micon"
        ariaLabel={item.labelKey ? undefined : item.key}
      />
    );
  }
  return (
    <span className="mingo-local-info-card__icon mingo-local-info-card__icon--row">
      {renderUiIcon(item.icon, { size: 16, stroke: 2, 'aria-hidden': true })}
    </span>
  );
}

function readFavSet() {
  try {
    const raw = window.localStorage.getItem(LS_FAV);
    if (!raw) return new Set();
    const a = JSON.parse(raw);
    if (!Array.isArray(a)) return new Set();
    return new Set(a.filter((x) => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

/** 구 음식점 바로가기(/restaurant) 찜 제거 */
function migrateLifeInfoFavSet(set) {
  const next = new Set(set);
  for (const k of next) {
    if (k.startsWith('/restaurant::') || k.endsWith('::restaurant')) {
      next.delete(k);
    }
  }
  return next;
}

function writeFavSet(set) {
  try {
    window.localStorage.setItem(LS_FAV, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

function readUiState() {
  try {
    const raw = window.sessionStorage.getItem(LS_UI);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeUiState(next) {
  try {
    window.sessionStorage.setItem(LS_UI, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function LocalInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, isFeatureEnabled, selectedRegions, activeRegionSlot, currentCountry, activeRegionView, language } = useMingo();
  const scrollRef = useRef(null);
  const restoredOnce = useRef(false);
  const uiSnapshot = useMemo(() => readUiState(), []);
  const defaultOpenBySection = useMemo(() => Object.fromEntries(HUB_SECTIONS.map((section) => [section.key, true])), []);
  const [favSet, setFavSet] = useState(() => {
    const initial = readFavSet();
    const migrated = migrateLifeInfoFavSet(initial);
    if (migrated.size !== initial.size) writeFavSet(migrated);
    return migrated;
  });
  const [emergencyOpen, setEmergencyOpen] = useState(uiSnapshot?.emergencyOpen ?? true);
  const [openBySection, setOpenBySection] = useState(uiSnapshot?.openBySection ?? defaultOpenBySection);

  useEffect(() => {
    const c = searchParams.get('category');
    if (c === 'move') {
      navigate('/local/cleaning', { replace: true });
    }
  }, [searchParams, navigate]);

  const openServiceMenuAll = useCallback(() => {
    writeUiState({
      emergencyOpen,
      openBySection,
      scrollTop: scrollRef.current?.scrollTop ?? 0,
    });
    navigate('/all-services', { state: withReturnState(location) });
  }, [emergencyOpen, location, navigate, openBySection]);

  const toggleFav = useCallback((e, c) => {
    e.preventDefault();
    e.stopPropagation();
    const k = favKey(c);
    setFavSet((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      writeFavSet(next);
      return next;
    });
  }, []);

  const labelFor = useCallback(
    (c) => c.label ?? (c.serviceV2ItemKey ? t(`serviceV2.items.${c.serviceV2ItemKey}`) : t(c.labelKey ?? `local.${c.key}`)),
    [t],
  );

  const descFor = useCallback(
    (c) => {
      const key = `business.catsDesc.${c.key}`;
      const v = t(key);
      return v && v !== key ? v : '';
    },
    [t],
  );

  const [listQuery, setListQuery] = useState('');
  const normalizedQuery = String(listQuery || '').trim().toLowerCase();
  const matchesItemSearch = useCallback(
    (item) => {
      if (!normalizedQuery) return true;
      let label = '';
      if (item.serviceV2ItemKey) label = t(`serviceV2.items.${item.serviceV2ItemKey}`)?.toLowerCase() || '';
      else label = t(item.labelKey ?? `local.${item.key}`)?.toLowerCase() || '';
      return label.includes(normalizedQuery) || String(item.to).toLowerCase().includes(normalizedQuery);
    },
    [normalizedQuery, t],
  );
  const itemsBySection = useMemo(
    () =>
      Object.fromEntries(
        HUB_SECTIONS.map((section) => [
          section.key,
          section.items.filter((item) => {
            const f = item.flag || CATEGORY_FLAG[item.key];
            if (f && !isFeatureEnabled(f)) return false;
            return matchesItemSearch(item);
          }),
        ]),
      ),
    [isFeatureEnabled, matchesItemSearch],
  );
  const allFavoritableItems = useMemo(() => {
    const merged = HUB_SECTIONS.flatMap((section) => itemsBySection[section.key] || []);
    const seen = new Set();
    return merged.filter((item) => {
      const k = favKey(item);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [itemsBySection]);
  const favItems = useMemo(() => allFavoritableItems.filter((item) => favSet.has(favKey(item))), [allFavoritableItems, favSet]);

  useEffect(() => {
    if (restoredOnce.current) return;
    restoredOnce.current = true;
    const y = Number(uiSnapshot?.scrollTop || 0);
    if (y > 0) {
      window.requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = y;
      });
    }
  }, [uiSnapshot]);

  useEffect(() => {
    writeUiState({
      emergencyOpen,
      openBySection,
      scrollTop: scrollRef.current?.scrollTop ?? 0,
    });
  }, [emergencyOpen, openBySection]);

  const saveUiSnapshot = useCallback(() => {
    writeUiState({
      emergencyOpen,
      openBySection,
      scrollTop: scrollRef.current?.scrollTop ?? 0,
    });
  }, [emergencyOpen, openBySection]);

  const navigateWithState = useCallback(
    (to) => {
      saveUiSnapshot();
      navigate(to);
    },
    [navigate, saveUiSnapshot],
  );

  const lifeLocation = useMemo(
    () => resolveLifeInfoLocation({ selectedRegions, activeRegionSlot, currentCountry, activeRegionView }),
    [selectedRegions, activeRegionSlot, currentCountry, activeRegionView],
  );
  const lifePack = useMemo(() => getSafePathLifePack(lifeLocation.regionKey), [lifeLocation.regionKey]);
  const lifeRegionLabel = useMemo(
    () => getLocalizedText(lifePack.label, lifePack.label?.ko || lifeLocation.displayLabel, language),
    [lifePack.label, lifeLocation.displayLabel, language],
  );
  const hasLifeCategories = lifePack.categories.length > 0;

  return (
    <div className="mingo-page mingo-page--stack mingo-local-info-hub mingo-section--premium">
      <div
        ref={scrollRef}
        className="mingo-page__scroll mingo-local-body mingo-local-body--life-compact"
        onScroll={saveUiSnapshot}
      >
        <div className="mingo-local-section mingo-local-section--emergency">
          <div className="mingo-local-section__title-row mingo-local-emergency__header">
            <button
              type="button"
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={() => setEmergencyOpen((v) => !v)}
              aria-expanded={emergencyOpen}
            >
              <h2 className="mingo-local-section__title">
                {t(SECTION_EMERGENCY.labelKey)}
              </h2>
              <span className={`mingo-local-section__more${emergencyOpen ? ' on' : ''}`} aria-hidden="true">
                ⌄
              </span>
            </button>
            <div className="mingo-local-emergency__quick">
              {SECTION_EMERGENCY.items.map(item => (
                <button
                  key={item.key}
                  type="button"
                  className="mingo-local-emergency__quick-btn"
                  onClick={() => navigateWithState(item.to)}
                >
                  <span>{renderUiIcon(item.icon, { size: 16, stroke: 2, 'aria-hidden': true })}</span>
                  <span>{t(item.labelKey ?? `local.${item.key}`)}</span>
                </button>
              ))}
            </div>
          </div>
          {emergencyOpen ? (
            <>
              {/* 교민 필수 정보 = expat-info 허브 */}
              <div className="mingo-expat-hub">
                <div className="mingo-expat-hub__header">
                  <div className="mingo-expat-hub__header-left">
                    <span className="mingo-expat-hub__header-icon"><IconBook size={20} stroke={2} aria-hidden /></span>
                    <span className="mingo-expat-hub__header-title">{t('local.expatInfoTitle')}</span>
                    <span className="mingo-expat-hub__meta mingo-expat-hub__meta--inline">
                      {t('life.safepathScopeCaption')}: {lifeRegionLabel}
                      {lifePack.inheritedFrom ? (
                        <span className="mingo-expat-hub__meta-note"> · {t('life.safepathInheritedNote')}</span>
                      ) : null}
                    </span>
                  </div>
                </div>
                {hasLifeCategories ? (
                  <>
                    <div className="mingo-expat-hub__grid">
                      {lifePack.categories.map((cat) => {
                        const icon = SAFE_PATH_SLUG_ICONS[cat.slug] || IconPin;
                        const label = getLocalizedText(cat.name, cat.name?.ko || cat.slug, language);
                        const to = `/safepath-life/${lifeLocation.country}/${lifeLocation.city}/${encodeURIComponent(cat.slug)}`;
                        return (
                          <Link key={cat.slug} to={to} className="mingo-expat-hub__cat-item" onClick={saveUiSnapshot}>
                            <span className="mingo-expat-hub__cat-icon">{renderUiIcon(icon, { size: 20 })}</span>
                            <span className="mingo-expat-hub__cat-label">{label}</span>
                          </Link>
                        );
                      })}
                    </div>
                    <div className="mingo-expat-hub__urgent-row">
                      {EXPAT_URGENT_ITEMS.map((item) => (
                        <Link key={item.key} to={item.to} className="mingo-expat-hub__urgent-item" onClick={saveUiSnapshot}>
                          <span>{renderUiIcon(item.icon, { size: 16, stroke: 2, 'aria-hidden': true })}</span>
                          <span>{t(item.labelKey)}</span>
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="mingo-biz-hint" style={{ margin: '8px 0 0', padding: '0 4px' }}>
                    {t('life.safepathComingSoonByRegion', { region: lifeRegionLabel || lifeLocation.displayLabel })}
                  </p>
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="mingo-local-section">
          <div style={{ padding: '0 4px 10px' }}>
            <input
              type="search"
              className="mingo-input"
              style={{ width: '100%', minHeight: 40 }}
              value={listQuery}
              onChange={(e) => setListQuery(e.target.value)}
              placeholder={t('life.searchPlaceholder')}
              aria-label={t('life.searchPlaceholder')}
              autoComplete="off"
            />
          </div>
          <div className="mingo-local-section__title-row">
            <h2 className="mingo-local-section__title">{t('life.favoritesSection')}</h2>
            <button
              type="button"
              className="mingo-local-section__more-btn"
              onClick={openServiceMenuAll}
            >
              {t('local.allServices')}
            </button>
          </div>
          {favItems.length > 0 ? (
            <ul className="all-services-list mingo-local-service-list" style={{ marginBottom: 14 }}>
              {favItems.map((c) => (
                <li key={`fav-${favKey(c)}`} className="mingo-local-service-row">
                  <button
                    type="button"
                    className="all-services-list__row mingo-local-service-row__main"
                    onClick={() => navigateWithState(c.to)}
                  >
                    <span className="all-services-list__icon">
                      <CardIcon item={c} />
                    </span>
                    <div className="all-services-list__body">
                      <span className="all-services-list__title">{labelFor(c)}</span>
                      {descFor(c) ? (
                        <span className="all-services-list__desc">{descFor(c)}</span>
                      ) : null}
                    </div>
                  </button>
                  <button
                    type="button"
                    className="mingo-local-service-row__star on"
                    aria-pressed="true"
                    aria-label={t('life.unfavorite')}
                    onClick={(e) => toggleFav(e, c)}
                  ><IconStar size={20} stroke={2} aria-hidden /></button>
                </li>
              ))}
            </ul>
          ) : null}
          {HUB_SECTIONS.map((section) => {
            const isOpen = openBySection[section.key] ?? true;
            const items = itemsBySection[section.key] || [];
            const q = normalizedQuery.trim();
            if (!items.length && !q) return null;
            return (
              <React.Fragment key={section.key}>
                <button
                  type="button"
                  className={`mingo-local-section__title-row mingo-local-hub-accordion${section.key === 'trade' ? ' mingo-local-hub-accordion--trade' : ''}${
                    section.key === 'life' ? ' mingo-local-hub-accordion--life' : ''
                  }`}
                  onClick={() =>
                    setOpenBySection((prev) => ({
                      ...prev,
                      [section.key]: !isOpen,
                    }))
                  }
                  aria-expanded={isOpen}
                >
                  <h3 className="mingo-local-hub-accordion__title">{t(section.titleKey)}</h3>
                  <span className={`mingo-local-section__more${isOpen ? ' on' : ''}`} aria-hidden="true">
                    ⌄
                  </span>
                </button>
                {isOpen ? (
                  items.length === 0 ? (
                    <p className="mingo-biz-hint" style={{ margin: '8px 0 14px', padding: '0 4px' }}>
                      {t('life.searchNoMatch')}
                    </p>
                  ) : (
                    <ul className="all-services-list mingo-local-service-list" style={{ marginBottom: 14 }}>
                      {items.map((item) => {
                        const on = favSet.has(favKey(item));
                        return (
                          <li key={`${item.to}::${item.key}`} className="mingo-local-service-row">
                            <button
                              type="button"
                              className="all-services-list__row mingo-local-service-row__main"
                              onClick={() => navigateWithState(item.to)}
                            >
                              <span className="all-services-list__icon">
                                <CardIcon item={item} />
                              </span>
                              <div className="all-services-list__body">
                                <span className="all-services-list__title">{labelFor(item)}</span>
                                {descFor(item) ? (
                                  <span className="all-services-list__desc">{descFor(item)}</span>
                                ) : null}
                              </div>
                            </button>
                            <button
                              type="button"
                              className={`mingo-local-service-row__star${on ? ' on' : ''}`}
                              aria-pressed={on}
                              aria-label={on ? t('life.unfavorite') : t('life.favorite')}
                              onClick={(e) => toggleFav(e, item)}
                            >
                              {on ? <IconStar size={14} stroke={2} aria-hidden /> : <IconStar size={14} stroke={2} aria-hidden />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default LocalInfo;
